import { Calendar, Users, ChevronRight, Pencil, Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ItineraryData } from "./models/itinerary.model";
import { ItineraryBuilder } from "./models/itinerary-builder.model";
import { getStorageThumbnailUrl } from "@/utils/imageStorage";

interface ItinerarySidebarProps {
  data: ItineraryData;
  builder?: ItineraryBuilder | null;
  onEditTrip?: () => void;
  onDaySelect?: (date: string) => void;
  selectedDate?: string;
  isReadOnly?: boolean;
}

const ItinerarySidebar = ({ data, builder, onEditTrip, onDaySelect, selectedDate, isReadOnly = false }: ItinerarySidebarProps) => {
  const { t } = useTranslation();

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateRange = () => {
    if (!data?.start_date || !data?.end_date) return '';
    const start = formatDate(data.start_date);
    const end = formatDate(data.end_date);
    return `${start} - ${end}`;
  };

  const generateMenuItems = () => {
    // Use builder if available for all days, otherwise fallback to data.itinerary_days
    const days = builder ? builder.getAllDays() : (data?.itinerary_days || []);
    
    return days.map(day => {
      const date = new Date(day.date);
      const dayNumber = date.getDate();
      const monthName = date.toLocaleDateString('it-IT', { month: 'short' });
      const dayName = date.toLocaleDateString('it-IT', { weekday: 'long' });

      return {
        id: day.date, // Use date as ID
        label: `${dayNumber} ${monthName}`,
        description: `${dayName} • ${t('sidebar.dayActivitiesCount', { count: day.activities.length })}`,
        date: day.date
      };
    });
  };

  const menuItems = generateMenuItems();

  return (
    // show on mobile (full width) and use side column on desktop
    <aside className="block w-full lg:w-80 h-full bg-white border-r border-gray-200 flex flex-col sticky top-0">
      {/* Header: compact on mobile (image - title - desc in one line) and full on desktop */}
      {/* Mobile compact header */}
      <div className="flex items-center gap-3 p-3 border-b lg:hidden">
        {data?.image ? (
          <div
            className="w-10 h-10 bg-cover bg-center rounded-md flex-shrink-0"
            style={{ backgroundImage: `url(${getStorageThumbnailUrl(data.image, { noTransform: true })})` }}
            aria-hidden
          />
        ) : (
          <div className="w-10 h-10 bg-gray-100 rounded-md flex-shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900 truncate">{data?.title || t('sidebar.untitledTrip')}</h3>
          <p className="mt-1 text-xs text-gray-500 truncate">{formatDateRange()} — {data?.total_guests} {data?.total_guests === 1 ? t('itinerary.guest') : t('itinerary.guests')}</p>
        </div>
        {!isReadOnly && onEditTrip && (
          <Button variant="ghost" size="icon" className="flex-shrink-0 h-8 w-8" onClick={onEditTrip}>
            <Pencil className="h-4 w-4 text-gray-500" />
          </Button>
        )}
        {isReadOnly && onEditTrip && (
          <Button variant="ghost" size="icon" className="flex-shrink-0 h-8 w-8" onClick={onEditTrip}>
            <Eye className="h-4 w-4 text-gray-500" />
          </Button>
        )}
      </div>

      {/* Mobile: horizontal days bar (scrollable) - keep it visible under compact header */}
      <div className="mt-2 mb-2 px-3 block lg:hidden">
        <div className="overflow-x-auto -mx-3 px-3 scrollbar-hide">
          <div className="flex gap-3 mb-2">
            {menuItems.map(item => (
              <button
                key={item.id}
                onClick={() => onDaySelect?.(item.date)}
                className={`flex-shrink-0 min-w-[50px] min-h-[40px] px-3 py-2 rounded-lg border transition-colors text-left ${selectedDate === item.date ? 'bg-gray-100 border-gray-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
              >
                <div className="text-sm font-medium text-gray-900">{item.label}</div>
                <div className="text-xs text-gray-500">{item.description}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Desktop full header */}
      <div className="hidden lg:block p-6 border-b border-gray-200">
        {data?.image && (
          <div className="mb-4">
            <div className="w-full h-20 bg-cover bg-center rounded-lg" style={{ backgroundImage: `url(${getStorageThumbnailUrl(data.image, { noTransform: true })})` }} />
          </div>
        )}
        <h2 className="text-lg font-semibold text-gray-900 mb-4 overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {data?.title || t('sidebar.untitledTrip')}
        </h2>
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span>{formatDateRange()}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Users className="h-4 w-4 text-gray-400" />
            <span>{data?.total_guests} {data?.total_guests === 1 ? t('itinerary.guest') : t('itinerary.guests')}</span>
          </div>
        </div>
        {onEditTrip && (
          <Button variant="outline" size="sm" className="mt-4 w-full flex items-center gap-2" onClick={onEditTrip}>
            {isReadOnly ? <Eye className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
            {isReadOnly ? t('sidebar.viewDetails') : t('sidebar.editDetails')}
          </Button>
        )}
      </div>

      {/* Navigation Menu - show only on desktop (vertical) */}
      <div className="flex-1 p-4 overflow-y-auto hidden lg:block">
        <nav className="space-y-1">
          {menuItems.length > 0 ? (
            menuItems.map((item) => (
              <button
                key={item.id}
                className={`w-full text-left p-3 rounded-lg transition-colors group ${selectedDate === item.date
                    ? 'bg-gray-100'
                    : 'hover:bg-gray-50'
                  }`}
                onClick={() => onDaySelect?.(item.date)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {item.label}
                    </div>
                    <div className="text-xs text-gray-500">
                      {item.description}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                </div>
              </button>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">{t('sidebar.selectDatesToSeeDays')}</p>
            </div>
          )}
        </nav>
      </div>
    </aside>
  );
};

export default ItinerarySidebar;
